class UnionFind {
  constructor(keywords) {
    this.parent = {};
    this.rank = {};
    keywords.forEach(keyword => {
      this.parent[keyword] = keyword;
      this.rank[keyword] = 0;
    });
  }

  find(x) {
    if (this.parent[x] !== x) {
      this.parent[x] = this.find(this.parent[x]);
    }
    return this.parent[x];
  }

  union(x, y) {
    const rootX = this.find(x);
    const rootY = this.find(y);
    
    if (rootX === rootY) return;
    
    if (this.rank[rootX] < this.rank[rootY]) {
      this.parent[rootX] = rootY;
    } else if (this.rank[rootX] > this.rank[rootY]) {
      this.parent[rootY] = rootX;
    } else {
      this.parent[rootY] = rootX;
      this.rank[rootX]++;
    }
  }
}

export function buildClusters(similarities, keywordInsights = []) {
  if (!similarities || similarities.length === 0) {
    const orphans = keywordInsights.map(insight => ({
      keyword: insight.keyword,
      volume: insight.volume || 0,
      position: insight.position,
      estimatedTraffic: insight.estimatedTraffic || 0,
      url: insight.url
    })).sort((a, b) => b.volume - a.volume);
    
    return { clusters: [], orphans };
  }

  const allKeywords = new Set();
  const keywordsInSimilarities = new Set();
  const volumeMap = {};
  const insightsMap = {};
  const similarityMap = new Map();

  keywordInsights.forEach(insight => {
    insightsMap[insight.keyword] = insight;
    volumeMap[insight.keyword] = insight.volume || 0;
    allKeywords.add(insight.keyword);
  });

  similarities.forEach(similarity => {
    keywordsInSimilarities.add(similarity.keyword1);
    keywordsInSimilarities.add(similarity.keyword2);
    allKeywords.add(similarity.keyword1);
    allKeywords.add(similarity.keyword2);
    if (!volumeMap[similarity.keyword1]) volumeMap[similarity.keyword1] = similarity.volume1 || 0;
    if (!volumeMap[similarity.keyword2]) volumeMap[similarity.keyword2] = similarity.volume2 || 0;
    
    const key1 = `${similarity.keyword1}|${similarity.keyword2}`;
    const key2 = `${similarity.keyword2}|${similarity.keyword1}`;
    similarityMap.set(key1, similarity.similarityScore);
    similarityMap.set(key2, similarity.similarityScore);
  });

  const unionFind = new UnionFind([...allKeywords]);

  similarities.forEach(similarity => {
    unionFind.union(similarity.keyword1, similarity.keyword2);
  });

  const clustersMap = {};
  allKeywords.forEach(keyword => {
    const root = unionFind.find(keyword);
    if (!clustersMap[root]) {
      clustersMap[root] = [];
    }
    clustersMap[root].push(keyword);
  });

  const clusters = Object.values(clustersMap)
    .filter(keywords => keywords.length > 1)
    .map(keywords => {
      const enrichedKeywords = keywords.map(keyword => {
        const insight = insightsMap[keyword] || {};
        return {
          keyword: keyword,
          volume: volumeMap[keyword] || 0,
          estimatedTraffic: insight.estimatedTraffic || 0,
          position: insight.position,
          url: insight.url
        };
      });

      enrichedKeywords.sort((a, b) => b.volume - a.volume);

      const mainKeyword = enrichedKeywords[0];
      
      const secondaryKeywords = enrichedKeywords.slice(1).map(keywordItem => {
        const directSimilarityScore = similarityMap.get(`${mainKeyword.keyword}|${keywordItem.keyword}`);
        
        if (directSimilarityScore !== undefined) {
          return {
            ...keywordItem,
            similarityScore: Math.round(directSimilarityScore)
          };
        }
        
        let bestSimilarity = 0;
        keywords.forEach(otherKeyword => {
          if (otherKeyword !== keywordItem.keyword) {
            const indirectSimilarityScore = similarityMap.get(`${otherKeyword}|${keywordItem.keyword}`);
            if (indirectSimilarityScore !== undefined && indirectSimilarityScore > bestSimilarity) {
              bestSimilarity = indirectSimilarityScore;
            }
          }
        });
        
        return {
          ...keywordItem,
          similarityScore: Math.round(bestSimilarity)
        };
      });

      const totalVolume = enrichedKeywords.reduce((sum, keywordItem) => sum + keywordItem.volume, 0);
      const totalTraffic = enrichedKeywords.reduce((sum, keywordItem) => sum + (keywordItem.estimatedTraffic || 0), 0);

      let totalSimilarity = 0;
      let pairCount = 0;
      for (let i = 0; i < keywords.length; i++) {
        for (let j = i + 1; j < keywords.length; j++) {
          const simScore = similarityMap.get(`${keywords[i]}|${keywords[j]}`);
          if (simScore !== undefined) {
            totalSimilarity += simScore;
            pairCount++;
          }
        }
      }
      const avgSimilarity = pairCount > 0 ? totalSimilarity / pairCount : 0;

      return {
        id: `cluster-${mainKeyword.keyword}`,
        mainKeyword: mainKeyword.keyword,
        mainVolume: mainKeyword.volume,
        mainPosition: mainKeyword.position,
        mainUrl: mainKeyword.url,
        mainTraffic: mainKeyword.estimatedTraffic,
        keywords: secondaryKeywords,
        totalVolume,
        totalTraffic,
        keywordCount: keywords.length,
        averageSimilarity: Math.round(avgSimilarity)
      };
    })
    .sort((a, b) => b.totalVolume - a.totalVolume);

  const orphans = [...allKeywords]
    .filter(keyword => {
      const root = unionFind.find(keyword);
      return !clustersMap[root] || clustersMap[root].length === 1;
    })
    .map(keyword => {
      const insight = insightsMap[keyword] || {};
      return {
        keyword: keyword,
        volume: volumeMap[keyword] || 0,
        position: insight.position,
        estimatedTraffic: insight.estimatedTraffic || 0,
        url: insight.url
      };
    })
    .sort((a, b) => b.volume - a.volume);

  return { clusters, orphans };
}

export function reclusterKeywords(memberKeywords, similarities, threshold, keywordInsights = []) {
  const memberSet = new Set(memberKeywords);

  const scopedSimilarities = (similarities || []).filter(similarity => {
    const bothInScope = memberSet.has(similarity.keyword1) && memberSet.has(similarity.keyword2);
    const aboveThreshold = similarity.similarityScore >= threshold;
    return bothInScope && aboveThreshold;
  });

  const scopedInsights = keywordInsights.filter(insight => memberSet.has(insight.keyword));

  return buildClusters(scopedSimilarities, scopedInsights);
}
